import { forwardRef } from 'react';
import moment from 'moment-timezone';
import { insuredColumn, ListTable, type ListColumn, type ListTableHandle } from '@/components/ListTable';
import { formatMoney } from '../../utils';
import { PolicyStatus, policyStatusLabel } from './PolicyStatus';

export type Policy = {
  id: number | string;
  insured?: string;
  policyNumber?: string;
  product?: { name?: string };
  region?: string;
  postcode?: string;
  coveredValue?: number;
  effectiveAt?: string;
  effectiveUntil?: string;
  payoutsEnabled?: boolean;
  canceledAt?: number | string | null;
};

export type PoliciesTableProps = {
  rows: Policy[];
  loading: boolean;
  /** Any request in flight, a cached refresh included; the next page waits for it (D29). */
  fetching?: boolean;
  /** The last request failed; a failed next page is asked for once more. */
  failed?: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
};

/** The React Native-style scroll command the search context sends on a search change. */
export type PoliciesTableHandle = ListTableHandle;

const policyPath = (policy: Policy) => `/policies/${policy.id}`;

const date = (value?: string) => (value ? moment.utc(value).format('MM-DD-YYYY') : 'N/A');

// Today's table-mode columns (PoliciesListView's defaultColDefs), fixed, without Region (D36). The
// widths add up to 796px, which fits the list at 1280px beside a classic 17px scrollbar, the insured
// taking the widest share.
const columns: ListColumn<Policy>[] = [
  { id: 'product', header: 'PRODUCT', size: 100, accessorFn: (policy) => policy.product?.name ?? '' },
  { id: 'policyNumber', header: 'Policy Number', size: 136, accessorFn: (policy) => policy.policyNumber || 'N/A' },
  insuredColumn<Policy>({ header: 'INSURED', insured: (policy) => policy.insured, path: policyPath, size: 140 }),
  {
    id: 'coveredValue',
    header: 'COVERED VALUE',
    size: 118,
    accessorFn: ({ coveredValue }) => (coveredValue ? `$${formatMoney(coveredValue)}` : 'N/A'),
  },
  { id: 'effectiveAt', header: 'START DATE', size: 98, accessorFn: ({ effectiveAt }) => date(effectiveAt) },
  { id: 'effectiveUntil', header: 'END DATE', size: 98, accessorFn: ({ effectiveUntil }) => date(effectiveUntil) },
  {
    id: 'active',
    header: 'ACTIVE',
    size: 106,
    accessorFn: (policy) => policyStatusLabel(policy),
    cell: ({ row }) => <PolicyStatus payoutsEnabled={row.original.payoutsEnabled} canceledAt={row.original.canceledAt} />,
  },
];

/** The Policies list's table (D15, D16, D29): the shared list table with today's table-mode columns. */
export const PoliciesTable = forwardRef<PoliciesTableHandle, PoliciesTableProps>(function PoliciesTable(props, ref) {
  return (
    <ListTable<Policy>
      ref={ref}
      {...props}
      columns={columns}
      rowPath={policyPath}
      rowId={(policy) => String(policy.id)}
      label="Policies"
      emptyMessage="No policies to show"
    />
  );
});
